import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import AuthModal from './AuthModal';
import AddLocationModal from './AddLocationModal';
import AddBusinessPinModal from './AddBusinessPinModal';
import RatingModal from './RatingModal';
import { BACKEND_URL, API_BASE } from '../config/api';

const API = API_BASE;

const Dashboard = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [userRatings, setUserRatings] = useState([]);
  const [userParks, setUserParks] = useState([]);
  const [parkNames, setParkNames] = useState({}); // Map of park_id -> park_name
  const [isLoading, setIsLoading] = useState(true);
  const [showAddLocationModal, setShowAddLocationModal] = useState(false);
  const [showAddBusinessPinModal, setShowAddBusinessPinModal] = useState(false);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [selectedPark, setSelectedPark] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);

  // Memoized calculations to prevent recalculation on every render
  const userStats = useMemo(() => {
    if (!stats?.user_stats) return null;
    return {
      totalRatings: userRatings.length,
      totalParks: userParks.length,
      averageRating: userRatings.length > 0 
        ? (userRatings.reduce((sum, r) => sum + r.rating, 0) / userRatings.length).toFixed(1)
        : '0.0',
      recentActivity: [...userRatings, ...userParks]
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 5)
    };
  }, [stats?.user_stats, userRatings, userParks]);

  // Memoized park groups for better performance
  const parkGroups = useMemo(() => {
    const now = new Date();
    return {
      editable: userParks.filter(park => {
        if (!park.edit_window_expires) return false;
        return new Date(park.edit_window_expires) > now;
      }),
      expired: userParks.filter(park => {
        if (!park.edit_window_expires) return true;
        return new Date(park.edit_window_expires) <= now;
      })
    };
  }, [userParks]);

  useEffect(() => {
    loadDashboardData();
  }, [isAuthenticated]);

  // Handle resend verification email
  const handleResendVerification = async () => {
    setResendingEmail(true);
    try {
      const response = await axios.post(`${API}/auth/resend-verification`, {
        email: user.email
      });
      
      setShowVerificationModal(true);
      toast.success('Verification email sent!');
    } catch (error) {
      console.error('Error resending verification:', error);
      toast.error(error.response?.data?.detail || 'Failed to send verification email');
    } finally {
      setResendingEmail(false);
    }
  };

  // Optimized dashboard data loading with parallel requests and caching
  const loadDashboardData = async () => {
    try {
      setIsLoading(true);
      console.log('🔄 Loading dashboard data (optimized)...');
      
      // Use Promise.all for parallel loading instead of sequential
      const promises = [
        axios.get(`${API}/dashboard/stats`).catch(e => ({ data: null, error: e }))
      ];

      if (isAuthenticated && user?.id) {
        promises.push(
          axios.get(`${API}/ratings/my`).catch(e => ({ data: [], error: e })),
          axios.get(`${API}/parks`, {
            params: { created_by: user.id, limit: 50 } // Add limit to prevent large data loads
          }).catch(e => ({ data: [], error: e }))
        );
      }

      const startTime = performance.now();
      const results = await Promise.all(promises);
      const loadTime = performance.now() - startTime;
      
      console.log(`✅ Dashboard data loaded in ${loadTime.toFixed(2)}ms`);
      
      // Process results
      const [statsResult, ratingsResult, parksResult] = results;
      
      if (statsResult && !statsResult.error) {
        setStats(statsResult.data);
      } else {
        console.warn('Stats loading failed:', statsResult?.error);
      }
      
      if (ratingsResult && !ratingsResult.error) {
        console.log('✅ User ratings loaded:', ratingsResult.data.length);
        setUserRatings(ratingsResult.data);
        
        // Fetch park names for each rating
        if (ratingsResult.data.length > 0) {
          fetchParkNamesForRatings(ratingsResult.data);
        }
      } else if (ratingsResult?.error) {
        console.warn('Ratings loading failed:', ratingsResult.error);
      }
      
      if (parksResult && !parksResult.error) {
        console.log('✅ User parks loaded:', parksResult.data.length);
        setUserParks(parksResult.data);
      } else if (parksResult?.error) {
        console.warn('Parks loading failed:', parksResult.error);
      }
      
    } catch (error) {
      console.error('❌ Error loading dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch park names for ratings
  const fetchParkNamesForRatings = async (ratings) => {
    try {
      const uniqueParkIds = [...new Set(ratings.map(r => r.park_id))];
      const nameMap = {};
      
      await Promise.all(
        uniqueParkIds.map(async (parkId) => {
          try {
            const response = await axios.get(`${API}/parks/${parkId}`);
            nameMap[parkId] = response.data.name || 'Unknown Park';
          } catch (error) {
            console.warn(`Failed to fetch park name for ${parkId}:`, error);
            nameMap[parkId] = 'Unknown Park';
          }
        })
      );
      
      setParkNames(nameMap);
      console.log('✅ Park names loaded:', Object.keys(nameMap).length);
    } catch (error) {
      console.error('Error fetching park names:', error);
    }
  };

  // Optimized refresh function - only refresh what's needed
  const refreshUserData = async () => {
    if (!isAuthenticated || !user?.id) return;
    
    try {
      console.log('🔄 Refreshing user data...');
      
      const [ratingsResult, parksResult] = await Promise.all([
        axios.get(`${API}/ratings/my`).catch(e => ({ data: userRatings, error: e })),
        axios.get(`${API}/parks`, {
          params: { created_by: user.id, limit: 50 }
        }).catch(e => ({ data: userParks, error: e }))
      ]);
      
      if (!ratingsResult.error) setUserRatings(ratingsResult.data);
      if (!parksResult.error) setUserParks(parksResult.data);
      
    } catch (error) {
      console.warn('⚠️ Error refreshing user data:', error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleDeletePark = useCallback(async (parkId, parkName) => {
    try {
      // Get auth token
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Please log in to delete parks');
        return;
      }
      
      const headers = { Authorization: `Bearer ${token}` };
      
      // First, get deletion info
      console.log('🔍 Fetching deletion info for park:', parkId);
      const infoResponse = await axios.get(`${API}/parks/${parkId}/deletion-info`, { headers });
      const { counts, is_popular } = infoResponse.data;
      
      // Build confirmation message with counts
      let confirmMessage = `Are you sure you want to delete "${parkName}"?\n\nThis will permanently delete:\n`;
      confirmMessage += `• The park location\n`;
      if (counts.comments > 0) confirmMessage += `• ${counts.comments} comment(s)\n`;
      if (counts.ratings > 0) confirmMessage += `• ${counts.ratings} rating(s)\n`;
      if (counts.reports > 0) confirmMessage += `• ${counts.reports} report(s)\n`;
      confirmMessage += `\n⚠️ This action cannot be undone.`;
      
      if (is_popular) {
        confirmMessage += `\n\n📊 Note: This is a popular park (>10 ratings). Your request will be sent to admin for review.`;
      }
      
      if (!window.confirm(confirmMessage)) {
        return;
      }

      console.log('🗑️ Deleting park:', parkId);
      const response = await axios.delete(`${API}/parks/${parkId}`, { headers });
      
      if (response.data.requires_approval) {
        toast.success('Deletion request submitted for admin review (popular park)');
      } else {
        const deletedCounts = response.data.deleted_counts;
        let successMsg = 'Park deleted successfully';
        if (deletedCounts && (deletedCounts.comments > 0 || deletedCounts.ratings > 0)) {
          successMsg += ` (${deletedCounts.comments} comments, ${deletedCounts.ratings} ratings removed)`;
        }
        toast.success(successMsg);
      }
      
      // Optimized: Only refresh user data instead of everything
      await refreshUserData();
    } catch (error) {
      console.error('❌ Error deleting park:', error);
      if (error.response?.status === 403) {
        toast.error(error.response.data.detail || 'Cannot delete this park');
      } else if (error.response?.status === 404) {
        toast.error('Park not found');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to delete park. Please try again.');
      }
    }
  }, [refreshUserData]); // Add dependency for useCallback

  // Loading skeleton component for better perceived performance
  const LoadingSkeleton = ({ className = "" }) => (
    <div className={`animate-pulse ${className}`}>
      <div className="bg-gray-200 rounded h-4 mb-2"></div>
      <div className="bg-gray-200 rounded h-4 mb-2 w-3/4"></div>
      <div className="bg-gray-200 rounded h-4 w-1/2"></div>
    </div>
  );

  const canDeletePark = useCallback((park) => {
    // Check if park has edit window expiry
    if (park.edit_window_expires) {
      const expiryDate = new Date(park.edit_window_expires);
      return new Date() < expiryDate;
    }
    
    // Legacy fallback for older parks without edit window
    if (!park.created_at) return false;
    
    const createdAt = new Date(park.created_at);
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    
    return createdAt > sevenDaysAgo;
  }, []); // No dependencies needed for canDeletePark

  const getRemainingEditTime = (park) => {
    if (park.edit_window_expires) {
      const expiryDate = new Date(park.edit_window_expires);
      const now = new Date();
      const diffInHours = Math.max(0, Math.floor((expiryDate - now) / (1000 * 60 * 60)));
      
      if (diffInHours > 24) {
        return `${Math.floor(diffInHours / 24)} days left`;
      } else if (diffInHours > 0) {
        return `${diffInHours} hours left`;
      } else {
        return 'Expired';
      }
    }
    
    // Legacy calculation
    if (park.created_at) {
      const createdAt = new Date(park.created_at);
      const sevenDaysLater = new Date(createdAt);
      sevenDaysLater.setDate(sevenDaysLater.getDate() + 7);
      const now = new Date();
      const diffInHours = Math.max(0, Math.floor((sevenDaysLater - now) / (1000 * 60 * 60)));
      
      if (diffInHours > 24) {
        return `${Math.floor(diffInHours / 24)} days left`;
      } else if (diffInHours > 0) {
        return `${diffInHours} hours left`;
      }
    }
    
    return 'Edit expired';
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 p-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500 mx-auto mb-4"></div>
              <h2 className="text-xl font-semibold text-gray-700">Loading Dashboard...</h2>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">
                {isAuthenticated ? `Welcome back, ${user?.username}! 🐾` : 'Happy Paws Dashboard 🐾'}
              </h1>
              <p className="text-gray-600 mt-1">
                {isAuthenticated 
                  ? 'Manage your ratings and locations' 
                  : 'Discover amazing dog walking spots in your community'
                }
              </p>
              
              {/* Unverified Email Warning - Show button */}
              {isAuthenticated && user && !user.is_verified && (
                <div className="mt-3 p-3 bg-amber-50 border-l-4 border-amber-500 rounded">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="text-amber-800 text-sm font-medium">
                        ⚠️ Email not verified
                      </p>
                      <p className="text-amber-700 text-xs mt-1">
                        Check your inbox or click to resend
                      </p>
                    </div>
                    <button
                      onClick={handleResendVerification}
                      disabled={resendingEmail}
                      className="bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-lg font-medium text-sm transition-colors disabled:opacity-50 whitespace-nowrap ml-3"
                    >
                      {resendingEmail ? '⏳ Sending...' : '📧 Resend Email'}
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div className="flex flex-wrap gap-2 justify-center md:justify-start">
              <button
                onClick={() => window.location.href = '/'}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-full font-medium transition-colors flex items-center justify-center space-x-1 text-sm md:text-base"
                data-testid="home-button"
              >
                <span>🏠</span><span>Home</span>
              </button>
              <button
                onClick={() => window.location.href = '/map'}
                className="bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2 rounded-full font-medium transition-colors flex items-center justify-center space-x-1 text-sm md:text-base"
                data-testid="view-map-button"
              >
                <span>🗺️</span><span>Explore Map</span>
              </button>
              {!isAuthenticated ? (
                <AuthModal defaultTab="register">
                  <button
                    className="bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2 rounded-full font-medium transition-colors flex items-center justify-center text-sm md:text-base"
                    data-testid="register-button"
                  >
                    <div className="flex flex-col items-center leading-tight">
                      <span>✨ Register</span>
                      <span className="text-xs">/ Login</span>
                    </div>
                  </button>
                </AuthModal>
              ) : (
                <button
                  onClick={() => setShowLogoutModal(true)}
                  className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-1.5 rounded-full font-medium transition-colors w-32 flex items-center justify-center space-x-1"
                  data-testid="logout-button"
                >
                  <span>🚪</span><span>Logout</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Interactive Stat Panels - Now serve as navigation tabs */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          {/* My Locations Panel */}
          <button
            onClick={() => setActiveTab('my-locations')}
            className={`bg-white rounded-xl p-6 shadow-lg transition-all duration-200 text-left
              ${activeTab === 'my-locations'
                ? 'ring-4 ring-emerald-500 ring-opacity-50 shadow-xl transform scale-105'
                : 'hover:shadow-xl hover:scale-105 cursor-pointer'
              }`}
            disabled={!isAuthenticated}
          >
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-600">Paw Parks Added</h3>
              <span className="text-2xl">🏞️</span>
            </div>
            <div className={`text-2xl font-bold ${activeTab === 'my-locations' ? 'text-emerald-700' : 'text-emerald-600'}`}>
              {isAuthenticated ? userParks.length : stats?.total_parks || 0}
            </div>
            <p className="text-xs text-gray-600 mt-1">
              {isAuthenticated ? 'Click to view your parks' : 'Dog-friendly locations'}
            </p>
          </button>

          {/* My Ratings Panel */}
          <button
            onClick={() => setActiveTab('my-ratings')}
            className={`bg-white rounded-xl p-6 shadow-lg transition-all duration-200 text-left
              ${activeTab === 'my-ratings'
                ? 'ring-4 ring-amber-500 ring-opacity-50 shadow-xl transform scale-105'
                : 'hover:shadow-xl hover:scale-105 cursor-pointer'
              }`}
            disabled={!isAuthenticated}
          >
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-600">Total Reviews</h3>
              <span className="text-2xl">⭐</span>
            </div>
            <div className={`text-2xl font-bold ${activeTab === 'my-ratings' ? 'text-amber-700' : 'text-amber-600'}`}>
              {isAuthenticated ? userRatings.length : stats?.total_ratings || 0}
            </div>
            <p className="text-xs text-gray-600 mt-1">
              {isAuthenticated ? 'Click to view your reviews' : 'Community feedback'}
            </p>
          </button>

          {/* Overview Panel */}
          <button
            onClick={() => setActiveTab('overview')}
            className={`bg-white rounded-xl p-6 shadow-lg transition-all duration-200 text-left
              ${activeTab === 'overview'
                ? 'ring-4 ring-blue-500 ring-opacity-50 shadow-xl transform scale-105'
                : 'hover:shadow-xl hover:scale-105 cursor-pointer'
              }`}
            disabled={!isAuthenticated}
          >
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-600">Happy Paws Community</h3>
              <span className="text-2xl">👥</span>
            </div>
            <div className={`text-2xl font-bold ${activeTab === 'overview' ? 'text-blue-700' : 'text-blue-600'}`}>
              {stats?.total_users || 0}
            </div>
            <p className="text-xs text-gray-600 mt-1">
              {isAuthenticated ? 'Click to view overview' : 'Community members'}
            </p>
          </button>

          {/* Activity Panel */}
          <button
            onClick={() => setActiveTab('overview')}
            className={`bg-white rounded-xl p-6 shadow-lg transition-all duration-200 text-left
              ${activeTab === 'overview'
                ? 'ring-4 ring-purple-500 ring-opacity-50 shadow-xl transform scale-105'
                : 'hover:shadow-xl hover:scale-105 cursor-pointer'
              }`}
            disabled={!isAuthenticated}
          >
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-600">Your Activity</h3>
              <span className="text-2xl">🎯</span>
            </div>
            <div className={`text-2xl font-bold ${activeTab === 'overview' ? 'text-purple-700' : 'text-purple-600'}`}>
              {isAuthenticated 
                ? (stats?.user_stats?.my_ratings || 0) + (stats?.user_stats?.my_parks || 0)
                : 0
              }
            </div>
            <p className="text-xs text-gray-600 mt-1">
              {isAuthenticated ? 'Click to view activity' : 'Login to contribute'}
            </p>
          </button>
        </div>

        {/* Main Content */}
        {isAuthenticated ? (
          <div className="space-y-6">
            {/* Two-Column Layout: Content + Quick Actions Sidebar */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              
              {/* Left Column: Content (2/3 width) - Tab navigation removed, now handled by stat panels above */}
              <div className="lg:col-span-2">
                <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                  {/* Tab Navigation REMOVED - Stats panels now serve as tabs */}
                  {/* OLD CODE (REVERTIBLE): Uncomment lines below to restore old tab menu
                  <div className="border-b border-gray-200">
                    <nav className="flex space-x-8 px-6">
                      <button onClick={() => setActiveTab('overview')} className={...}>Overview</button>
                      <button onClick={() => setActiveTab('my-ratings')} className={...}>My Ratings ({userRatings.length})</button>
                      <button onClick={() => setActiveTab('my-locations')} className={...}>My Locations ({userParks.length})</button>
                    </nav>
                  </div>
                  */}

                  <div className="p-6">
                    {activeTab === 'overview' && (
                      <div className="space-y-6">
                        {/* User Profile Card - Full Width */}
                        <div className="bg-gray-50 rounded-lg p-6">
                      <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                        <span className="mr-2">👤</span>
                        Profile Overview
                      </h3>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-gray-600">Username:</span>
                          <span className="bg-emerald-100 text-emerald-800 px-2 py-1 rounded text-sm font-medium">
                            {user?.username}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-gray-600">Email Status:</span>
                          <span className={`px-2 py-1 rounded text-sm font-medium ${
                            user?.is_verified 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {user?.is_verified ? '✅ Verified' : '❌ Unverified'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-gray-600">Member Since:</span>
                          <span className="font-medium">{formatDate(user?.created_at)}</span>
                        </div>
                        <div className="pt-4 border-t border-gray-200">
                          <div className="grid grid-cols-2 gap-4 text-center">
                            <div>
                              <div className="text-2xl font-bold text-emerald-600">
                                {userRatings.length}
                              </div>
                              <div className="text-sm text-gray-600">Reviews</div>
                            </div>
                            <div>
                              <div className="text-2xl font-bold text-blue-600">
                                {userParks.length}
                              </div>
                              <div className="text-sm text-gray-600">Locations Added</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                      </div>
                    )}

                {activeTab === 'my-ratings' && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">My Ratings</h3>
                    {userRatings.length === 0 ? (
                      <div className="text-center py-8">
                        <p className="text-gray-500 mb-4">You haven't rated any locations yet.</p>
                        <button 
                          className="bg-emerald-500 hover:bg-emerald-600 text-white py-2 px-4 rounded-lg font-medium transition-colors"
                          onClick={() => setShowRatingModal(true)}
                        >
                          Rate Your First Location
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {userRatings.map((rating) => (
                          <div
                            key={rating.id}
                            onClick={() => window.location.href = `/map?parkId=${rating.park_id}`}
                            className="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:border-emerald-400 hover:shadow-md cursor-pointer transition-all"
                          >
                            <div className="flex items-start justify-between mb-2">
                              <h4 className="font-medium text-gray-800 hover:text-emerald-600 transition-colors">
                                {parkNames[rating.park_id] || 'Loading park name...'}
                              </h4>
                              <div className="flex items-center space-x-1">
                                <span className="text-yellow-500">★</span>
                                <span className="font-bold">{rating.rating.toFixed(1)}</span>
                              </div>
                            </div>
                            {rating.comment && (
                              <p className="text-gray-600 text-sm mb-2">
                                "{rating.comment}"
                              </p>
                            )}
                            <p className="text-xs text-gray-500">
                              Rated on {formatDate(rating.created_at)}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'my-locations' && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">My Locations</h3>
                    {userParks.length === 0 ? (
                      <div className="text-center py-8">
                        <p className="text-gray-500 mb-4">You haven't added any locations yet.</p>
                        {user?.is_verified ? (
                          <button 
                            className="bg-emerald-500 hover:bg-emerald-600 text-white py-2 px-4 rounded-lg font-medium transition-colors"
                            onClick={() => setShowAddLocationModal(true)}
                          >
                            Add Your First Location
                          </button>
                        ) : (
                          <p className="text-amber-600 text-sm">
                            Verify your email to add new locations.
                          </p>
                        )}
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {userParks.map((park) => (
                          <div
                            key={park.id}
                            className="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:shadow-md transition-shadow relative"
                          >
                            {/* Main content - clickable */}
                            <div 
                              className="cursor-pointer"
                              onClick={() => window.location.href = `/map?park=${park.id}`}
                            >
                              <div className="flex items-start justify-between mb-2">
                                <h4 className="font-medium text-gray-800 truncate pr-2">
                                  {park.name}
                                </h4>
                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                  park.verified 
                                    ? 'bg-green-100 text-green-800' 
                                    : 'bg-yellow-100 text-yellow-800'
                                }`}>
                                  {park.verified ? '✅ Verified' : '⏳ Pending'}
                                </span>
                              </div>
                              {park.description && (
                                <p className="text-gray-600 text-sm mb-2 line-clamp-2">
                                  {park.description}
                                </p>
                              )}
                              <div className="flex items-center justify-between text-sm">
                                <div className="flex items-center space-x-1">
                                  <span className="text-yellow-500">★</span>
                                  <span>{park.rating.toFixed(1)}</span>
                                  <span className="text-gray-500">
                                    ({park.rating_count})
                                  </span>
                                </div>
                                <span className="text-gray-500">
                                  Added {formatDate(park.created_at)}
                                </span>
                              </div>
                            </div>
                            
                            {/* Edit window status and delete button */}
                            <div className="absolute top-2 right-2 flex items-center space-x-1">
                              {canDeletePark(park) ? (
                                <>
                                  <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full">
                                    {getRemainingEditTime(park)}
                                  </span>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeletePark(park.id, park.name);
                                    }}
                                    className="bg-red-500 hover:bg-red-600 text-white text-xs px-2 py-1 rounded-full opacity-75 hover:opacity-100 transition-opacity"
                                    title={`Delete park (${getRemainingEditTime(park)})`}
                                  >
                                    🗑️
                                  </button>
                                </>
                              ) : (
                                <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                                  Community Owned
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
              </div>
              
              {/* Right Column: Quick Actions Sidebar (1/3 width) - Always Visible */}
              <div className="lg:col-span-1">
                <div className="bg-white rounded-xl shadow-lg overflow-hidden sticky top-6">
                  <div className="bg-gray-50 p-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                      <span className="mr-2">⚡</span>
                      Quick Actions
                    </h3>
                    <div className="space-y-3">
                      {user?.is_verified ? (
                        <>
                          <button 
                            className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg font-medium transition-colors"
                            data-testid="add-location-button"
                            onClick={() => setShowAddLocationModal(true)}
                          >
                            📍 Add New Location
                          </button>
                          
                          <button 
                            className="w-full bg-amber-600 hover:bg-amber-700 text-white py-2 px-4 rounded-lg font-medium transition-colors"
                            data-testid="add-rating-button"
                            onClick={() => setShowRatingModal(true)}
                          >
                            ⭐ Rate a Location
                          </button>
                          
                          <button
                            className="w-full bg-emerald-500 hover:bg-emerald-600 text-white py-2 px-4 rounded-lg font-medium transition-colors"
                            onClick={() => window.location.href = '/map'}
                          >
                            🗺️ Explore Map
                          </button>
                        </>
                      ) : (
                        <div className="text-center p-4 bg-amber-50 rounded-lg border border-amber-200">
                          <p className="text-amber-800 font-medium mb-2">
                            📧 Email Verification Required
                          </p>
                          <p className="text-sm text-amber-700 mb-3">
                            Please verify your email to add locations and ratings.
                          </p>
                          <button
                            onClick={handleResendVerification}
                            disabled={resendingEmail}
                            className="bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-lg font-medium text-sm transition-colors disabled:opacity-50"
                          >
                            {resendingEmail ? '⏳ Sending...' : '📧 Resend Verification Email'}
                          </button>
                        </div>
                      )}
                      
                      {/* Admin Panel Button - Always visible for admin/master, regardless of verification */}
                      {(user?.role === 'admin' || user?.role === 'master') && (
                        <>
                          <button
                            onClick={() => window.location.href = '/admin'}
                            className="w-full bg-purple-600 hover:bg-purple-700 text-white py-2 px-4 rounded-lg font-medium transition-colors mt-3"
                          >
                            🛡️ Admin Panel
                          </button>
                          
                          {/* Add Business Pin Button - Admin only */}
                          <button
                            onClick={() => setShowAddBusinessPinModal(true)}
                            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-2 px-4 rounded-lg font-medium transition-colors mt-3 flex items-center justify-center gap-2"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            Add Business Pin
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          // Non-authenticated view
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Join the Happy Paws Community</h2>
              <p className="text-gray-600 mb-8">
                Create an account to add locations, rate parks, and connect with fellow dog lovers
              </p>

              {/* Top Parks for public view */}
              {stats?.top_parks && stats.top_parks.length > 0 && (
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">🏆 Top Rated Parks</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {stats.top_parks.slice(0, 4).map((park, index) => (
                      <div
                        key={park.id}
                        className="bg-gray-50 rounded-lg p-4 border border-gray-200 cursor-pointer hover:shadow-md transition-shadow"
                        onClick={() => window.location.href = `/map?park=${park.id}`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <h4 className="font-medium text-gray-800 truncate pr-2">
                            {park.name}
                          </h4>
                          <span className="bg-emerald-100 text-emerald-800 px-2 py-1 rounded text-xs font-medium">
                            #{index + 1}
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <div className="flex items-center space-x-1">
                            <span className="text-yellow-500">★</span>
                            <span className="font-medium">{park.rating.toFixed(1)}</span>
                          </div>
                          <span className="text-gray-500">
                            {park.rating_count} review{park.rating_count !== 1 ? 's' : ''}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-center space-x-4">
                <button
                  onClick={() => window.location.href = '/map'}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white px-6 py-3 rounded-lg font-medium transition-colors"
                >
                  🗺️ Explore Map
                </button>
                <button
                  onClick={() => window.location.href = '/'}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-3 rounded-lg font-medium transition-colors"
                >
                  🏠 Back to Home
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Add Location Modal */}
      {showAddLocationModal && (
        <AddLocationModal
          isOpen={showAddLocationModal}
          onClose={() => {
            setShowAddLocationModal(false);
            loadDashboardData(); // Refresh data after adding
          }}
        />
      )}

      {/* Rating Modal */}
      {showRatingModal && (
        <RatingModal
          isOpen={showRatingModal}
          onClose={() => {
            setShowRatingModal(false);
            loadDashboardData(); // Refresh data after rating
          }}
          park={selectedPark}
        />
      )}

      {/* Logout Confirmation Modal */}
      {showLogoutModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <div className="text-center">
              <div className="text-4xl mb-4">🚪</div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Confirm Logout</h3>
              <p className="text-gray-600 mb-6">Are you sure you want to logout?</p>
              
              {/* Remember Me Option */}
              <div className="flex items-center justify-center mb-6">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="rounded border-gray-300 text-emerald-500 focus:ring-emerald-500"
                  />
                  <span className="text-sm text-gray-700">Remember me next time I open the app</span>
                </label>
              </div>
              
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowLogoutModal(false)}
                  className="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-2 px-4 rounded-lg font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (rememberMe) {
                      // Store remember me preference
                      localStorage.setItem('rememberMe', 'true');
                      localStorage.setItem('lastUsername', user?.username || '');
                      // Note: In production, this should be more secure
                      const lastPassword = localStorage.getItem('tempPassword') || '';
                      localStorage.setItem('lastPassword', lastPassword);
                    } else {
                      localStorage.removeItem('rememberMe');
                      localStorage.removeItem('lastUsername');
                      localStorage.removeItem('lastPassword');
                    }
                    setShowLogoutModal(false);
                    logout();
                  }}
                  className="flex-1 bg-orange-600 hover:bg-orange-700 text-white py-2 px-4 rounded-lg font-medium transition-colors"
                >
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Verification Email Sent Modal */}
      {showVerificationModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 relative">
            <button
              onClick={() => setShowVerificationModal(false)}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-orange-500 hover:bg-orange-600 text-white transition-colors"
            >
              ×
            </button>
            
            <div className="text-center">
              <div className="text-5xl mb-4">📧</div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">
                Check Your Email!
              </h3>
              <p className="text-gray-600 mb-4">
                We've sent a verification link to <strong>{user?.email}</strong>
              </p>
              <p className="text-sm text-gray-500 mb-6">
                Click the link in the email to verify your account. Don't forget to check your spam folder!
              </p>
              <button
                onClick={() => setShowVerificationModal(false)}
                className="bg-emerald-500 hover:bg-emerald-600 text-white px-6 py-2 rounded-lg font-medium transition-colors"
              >
                Got it!
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Business Pin Modal */}
      <AddBusinessPinModal
        isOpen={showAddBusinessPinModal}
        onClose={() => setShowAddBusinessPinModal(false)}
        onPinCreated={(newPin) => {
          // Store new business pin context in localStorage for MapPage
          const businessPinContext = {
            id: newPin.id,
            lat: newPin.latitude,
            lng: newPin.longitude,
            zoom: newPin.zoom || 15, // Default zoom if not provided
            category: newPin.category,
            timestamp: Date.now()
          };
          localStorage.setItem("newBusinessPin", JSON.stringify(businessPinContext));
          
          toast.success("Business pin created! Click the pin to add offer details.");
          setTimeout(() => {
            window.location.href = "/map";
          }, 1500);
        }}
      />
    </div>
  );
};

export default Dashboard;